import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AdminProvider, useAdmin } from "@/lib/admin";
import { I18nProvider } from "@/lib/i18n";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import NewDesign from "@/pages/NewDesign";
import Results from "@/pages/Results";
import Orders from "@/pages/Orders";
import Settings from "@/pages/Settings";
import Share from "@/pages/Share";
import Layout from "@/components/Layout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import Factories from "@/pages/admin/Factories";
import FactoryDetail from "@/pages/admin/FactoryDetail";
import AdminOrders from "@/pages/admin/AdminOrders";
import AiInsights from "@/pages/admin/AiInsights";
import Plans from "@/pages/admin/Plans";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AdminProtected({ children }) {
  const { admin, loading } = useAdmin();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <AdminProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors />
            <Routes>
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/share/:token" element={<Share />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminProtected><AdminDashboard /></AdminProtected>} />
              <Route path="/admin/factories" element={<AdminProtected><Factories /></AdminProtected>} />
              <Route path="/admin/factories/:id" element={<AdminProtected><FactoryDetail /></AdminProtected>} />
              <Route path="/admin/orders" element={<AdminProtected><AdminOrders /></AdminProtected>} />
              <Route path="/admin/ai-insights" element={<AdminProtected><AiInsights /></AdminProtected>} />
              <Route path="/admin/plans" element={<AdminProtected><Plans /></AdminProtected>} />
              <Route path="/" element={<Protected><Home /></Protected>} />
              <Route path="/new" element={<Protected><NewDesign /></Protected>} />
              <Route path="/results/:id" element={<Protected><Results /></Protected>} />
              <Route path="/orders" element={<Protected><Orders /></Protected>} />
              <Route path="/settings" element={<Protected><Settings /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AdminProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
